import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import requestLoyaltyCustomerActionRewards from 'api/loyalty/requestLoyaltyCustomerActionRewards'
import { requestLoyaltyActionRewardsEarnCalculationBatch } from 'api/loyalty/requestLoyaltyActionRewardsEarnCalculation'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import {
  classifyLuxPlusRenewalSteps,
  getNextLuxPlusRenewalRewardYear,
  sumRenewalStatusCredits,
  LUX_PLUS_RENEWAL_ACTIONS,
  LUX_PLUS_RENEWAL_FALLBACK_AMOUNTS,
  type LuxPlusRenewalAction,
  type LuxPlusRenewalStepState,
} from 'loyalty/lib/luxPlusRenewalActions'

export interface LuxPlusRenewalBonusStep {
  /** 1-based position — 1st / 2nd / 3rd+ renewal. */
  year: number
  state: LuxPlusRenewalStepState
  points: number | undefined
  statusCredits: number | undefined
}

/**
 * The member's upcoming renewal reward — the step classified as `current`, falling back to the
 * first step when none is current yet (e.g. a member with no active progression). Shared by the
 * surfaces that headline a single reward amount (renewal-bonus retention + cancellation success).
 */
export function getCurrentRenewalRewardStep(
  steps: Array<LuxPlusRenewalBonusStep>,
): LuxPlusRenewalBonusStep | undefined {
  return steps.find((step) => step.state === 'current') ?? steps[0]
}

interface RenewalBonusAmount {
  points: number | undefined
  statusCredits: number | undefined
}

type AmountsByAction = Partial<Record<LuxPlusRenewalAction, RenewalBonusAmount>>

const RENEWAL_ACTION_SET: ReadonlySet<string> = new Set(LUX_PLUS_RENEWAL_ACTIONS)

// The "1st Renewal" tier — only earned at the start of a run, so it marks a run boundary.
const FIRST_RENEWAL_ACTION: LuxPlusRenewalAction = LUX_PLUS_RENEWAL_ACTIONS[0]

function isRenewalAction(action: string): action is LuxPlusRenewalAction {
  return RENEWAL_ACTION_SET.has(action)
}

/**
 * Amounts actually earned on the completed steps, scoped to the member's current
 * consecutive renewal streak.
 *
 * Claims are walked newest → oldest and collected until (and including) the first
 * `LuxPlusRenewal` claim. That identifier is the "1st Renewal" tier, which can only be
 * earned at the start of a run, so it marks the boundary — anything older belongs to a
 * previous (lapsed) run and is excluded. Amounts are then deduped by identifier, keeping
 * the newest claim per tier (e.g. repeated 3YR years).
 *
 * If the collected run has more claims than the member's completed eligible years, the
 * data is inconsistent (e.g. the current run has no year-1 claim, so the walk spilled into
 * an older run) — return nothing rather than surface a wrong amount.
 */
interface EarnedRenewalRewards {
  /** Amounts per tier for display, deduped by identifier (at most 3 entries). */
  amounts: AmountsByAction
  /** Actual number of claims in the current run — not deduped, so it can exceed 3. */
  claimedCount: number
}

function buildEarnedAmounts(
  actionRewards: Array<App.LoyaltyActionReward>,
  consecutiveCompletedYears: number,
): EarnedRenewalRewards {
  if (consecutiveCompletedYears < 1) return { amounts: {}, claimedCount: 0 }

  const renewalClaims = actionRewards
    .flatMap((reward) => reward.appliedRecords)
    .filter((record) => isRenewalAction(record.action))
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())

  const streak: Array<App.LoyaltyActionRewardAppliedRecord> = []
  for (const record of renewalClaims) {
    streak.push(record)
    // First "1st Renewal" claim = start of the current run; stop once it's included.
    if (record.action === FIRST_RENEWAL_ACTION) break
  }

  if (streak.length > consecutiveCompletedYears) return { amounts: {}, claimedCount: 0 }

  const amounts: AmountsByAction = {}
  for (const record of streak) {
    // Keep the newest claim per identifier within the streak (e.g. repeated 3YR years).
    if (!isRenewalAction(record.action) || amounts[record.action]) continue
    amounts[record.action] = {
      points: record.bonusPoints,
      statusCredits: sumRenewalStatusCredits(record.statusCredits, record.bonusStatusCredits),
    }
  }
  return { amounts, claimedCount: streak.length }
}

interface RenewalBonusData {
  actionRewards: Array<App.LoyaltyActionReward>
  estimated: AmountsByAction
}

const EMPTY_DATA: RenewalBonusData = { actionRewards: [], estimated: {} }

interface UseLuxPlusRenewalBonusResult {
  steps: Array<LuxPlusRenewalBonusStep>
  isLoading: boolean
  shouldHideRenewalProgression: boolean
}

/**
 * Assembles the 3-step annual renewal bonus timeline (1st / 2nd / 3rd+):
 * - which step is current comes from the member's `renewalRewardEligibleYears`,
 * - completed steps' amounts come from action-reward history (actually earned),
 * - current/upcoming steps' amounts come from the action-reward earn calc.
 */
export default function useLuxPlusRenewalBonus(): UseLuxPlusRenewalBonusResult {
  const customerId = useAppSelector((state) => state.auth.account.memberId)
  const subscription = useAppSelector((state) => state.auth.account.luxPlus.member.subscription.item)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const region = subscription?.region

  const nextRenewalRewardYear = getNextLuxPlusRenewalRewardYear(subscription)
  const consecutiveCompletedYears = subscription?.renewalRewardEligibleYears ?? 0

  const [data, setData] = useState<RenewalBonusData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!customerId) return

    let active = true
    setIsLoading(true)

    // Fetch history and earn independently — a failure in one (e.g. a member with no
    // action-reward history) must not discard the other's amounts.
    const historyPromise = requestLoyaltyCustomerActionRewards(customerId)
      .catch(() => [] as Array<App.LoyaltyActionReward>)

    const earnPromise = requestLoyaltyActionRewardsEarnCalculationBatch(
      customerId,
      LUX_PLUS_RENEWAL_ACTIONS.map((action) => ({
        action,
        metadata: region ? { region } : undefined,
      })),
    )
      .then((earnResults) => {
        const estimated: AmountsByAction = {}
        for (const result of earnResults) {
          if (result.status === 'success' && isRenewalAction(result.action)) {
            estimated[result.action] = {
              points: result.bonusPoints,
              statusCredits: sumRenewalStatusCredits(result.statusCredits, result.bonusStatusCredits),
            }
          }
        }
        return estimated
      })
      .catch(() => ({} as AmountsByAction))

    Promise.all([historyPromise, earnPromise])
      .then(([actionRewards, estimated]) => {
        if (!active) return
        setData({ actionRewards, estimated })
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [customerId, region])

  const { steps, shouldHideRenewalProgression } = useMemo(() => {
    // Completed amounts are streak-scoped here so they track renewalRewardEligibleYears
    // without needing to refetch. `claimedCount` is the real run length (not deduped).
    const { amounts: earned, claimedCount } = buildEarnedAmounts(data.actionRewards, consecutiveCompletedYears)

    // Missed a previous reward = fewer claims in the current run than the rewards a healthy member
    // should already hold. Rewards are earned *on renewal*, so the current/pending renewal's reward
    // isn't claimed yet — the number already due is `nextRenewalRewardYear - 1` (not the completed-
    // year count, which would over-count by one for a member awaiting their renewal).
    const hasMissedPreviousReward = claimedCount < nextRenewalRewardYear - 1

    // Collapse the step ladder into a single "next renewal" headline when there's no
    // meaningful progression to show: the member is past the 3-tier ladder (4th+ renewal),
    // is an Infinite member (no status credits), or has a gap in their streak.
    const shouldHideRenewalProgression =
      nextRenewalRewardYear > LUX_PLUS_RENEWAL_ACTIONS.length || isInfinite || hasMissedPreviousReward

    // Logged-out visitors can't call the authenticated earn endpoints, so fall back to
    // the static tier amounts. Logged-in customers always use the live calc-earn results.
    const estimated = customerId ? data.estimated : LUX_PLUS_RENEWAL_FALLBACK_AMOUNTS

    const steps = classifyLuxPlusRenewalSteps(nextRenewalRewardYear).map((step): LuxPlusRenewalBonusStep => {
      const amount = step.state === 'completed'
        ? earned[step.identifier]
        : estimated[step.identifier]

      return {
        year: step.year,
        state: step.state,
        points: amount?.points,
        statusCredits: amount?.statusCredits,
      }
    })

    return { steps, shouldHideRenewalProgression }
  }, [nextRenewalRewardYear, consecutiveCompletedYears, data, customerId, isInfinite])

  return { steps, isLoading, shouldHideRenewalProgression }
}
